import type { LeafRoute, RedirectRoute, RouteDef, EnumConstraint } from './RenderRoutes';
import { joinPath } from './utils';

// ─── Path-string param extraction (template-literal types) ───────────────────
// Walks a path string and extracts the names of `:param` tokens. Param names
// follow the identifier rules used by `extractParamNames` in RenderRoutes.tsx:
// leading letter or `_`, then letters / digits / `_`.

type Letter =
  | 'a'
  | 'b'
  | 'c'
  | 'd'
  | 'e'
  | 'f'
  | 'g'
  | 'h'
  | 'i'
  | 'j'
  | 'k'
  | 'l'
  | 'm'
  | 'n'
  | 'o'
  | 'p'
  | 'q'
  | 'r'
  | 's'
  | 't'
  | 'u'
  | 'v'
  | 'w'
  | 'x'
  | 'y'
  | 'z'
  | 'A'
  | 'B'
  | 'C'
  | 'D'
  | 'E'
  | 'F'
  | 'G'
  | 'H'
  | 'I'
  | 'J'
  | 'K'
  | 'L'
  | 'M'
  | 'N'
  | 'O'
  | 'P'
  | 'Q'
  | 'R'
  | 'S'
  | 'T'
  | 'U'
  | 'V'
  | 'W'
  | 'X'
  | 'Y'
  | 'Z';
type Digit = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
type WordChar = Letter | Digit | '_';

type TakeWord<S extends string> = S extends `${infer Head}${infer Tail}`
  ? Head extends WordChar
    ? `${Head}${TakeWord<Tail>}`
    : ''
  : '';

type RestAfterWord<S extends string> = S extends `${infer Head}${infer Tail}`
  ? Head extends WordChar
    ? RestAfterWord<Tail>
    : S
  : '';

type ExtractParam<S extends string> = S extends `${string}:${infer Rest}`
  ? TakeWord<Rest> extends ''
    ? never
    : TakeWord<Rest> | ExtractParam<RestAfterWord<Rest>>
  : never;

// ─── Path joining (type-level mirror of utils.joinPath) ─────────────────────

type StripLeadingSlash<S extends string> = S extends `/${infer R}` ? R : S;
type StripTrailingSlash<S extends string> = S extends `${infer R}/` ? (R extends '' ? '/' : R) : S;
type JoinPath<P extends string, C extends string> = P extends ''
  ? C
  : C extends ''
  ? P
  : P extends '/'
  ? C extends `/${string}`
    ? C
    : `/${C}`
  : `${StripTrailingSlash<P>}/${StripLeadingSlash<C>}`;

// ─── Param-value typing ──────────────────────────────────────────────────────

type ConstraintValue<C> = C extends EnumConstraint<infer V> ? V : C extends 'uuid' ? string : string | number;

type ValueFor<Name extends string, Constraints> = Name extends keyof Constraints
  ? ConstraintValue<Constraints[Name]>
  : string | number;

type ParamsOf<Path extends string, OptionalNames extends string, Constraints> = {
  [K in Exclude<ExtractParam<Path>, OptionalNames>]: ValueFor<K, Constraints>;
} & {
  [K in Extract<ExtractParam<Path>, OptionalNames>]?: ValueFor<K, Constraints>;
};

// `{}` for routes without params, the params object otherwise.
type IsNever<T> = [T] extends [never] ? true : false;
type HelperFn<Path extends string, OptionalNames extends string, Constraints> = IsNever<ExtractParam<Path>> extends true
  ? () => string
  : (params: ParamsOf<Path, OptionalNames, Constraints>) => string;

// ─── Tree → nested helper shape ──────────────────────────────────────────────

type LeafKey<D extends RouteDef> = D extends RedirectRoute
  ? never
  : D extends { key: infer K extends string }
  ? K
  : never;

type OptionalNamesOf<D> = D extends { optionalParams: infer O extends readonly string[] } ? O[number] : never;

type ConstraintsOf<D> = D extends { paramConstraints: infer C } ? C : unknown;

// `Helper` and `NodeFor` recurse through each other (a node owns child nodes,
// each child node is built from a Helper sub-tree). Declaration order is
// arbitrary; silence the lint at the one unavoidable forward reference.
type Helper<Defs extends readonly RouteDef[], Parent extends string = ''> = {
  [D in Defs[number] as LeafKey<D>]: D extends LeafRoute ? NodeFor<D, Parent> : never; // eslint-disable-line no-use-before-define
};

type NodeFor<D extends LeafRoute, Parent extends string> = D extends {
  path: infer P extends string;
  children?: infer Ch;
}
  ? { link: HelperFn<JoinPath<Parent, P>, OptionalNamesOf<D>, ConstraintsOf<D>> } & (Ch extends readonly RouteDef[]
      ? Helper<Ch, JoinPath<Parent, P>>
      : unknown)
  : never;

// ─── Runtime walker ──────────────────────────────────────────────────────────

const interpolate = (path: string, params: Record<string, string | number> = {}): string => {
  const filled = path.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_match, name: string) => {
    const value = params[name];
    return value === undefined ? '' : String(value);
  });
  const collapsed = filled.replace(/\/{2,}/g, '/');
  const trimmed = collapsed.length > 1 && collapsed.endsWith('/') ? collapsed.slice(0, -1) : collapsed;
  return trimmed || '/';
};

const isRedirect = (def: RouteDef): def is RedirectRoute => 'redirect' in def;

const walk = (defs: readonly RouteDef[], parentPath = ''): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  defs.forEach((def) => {
    if (isRedirect(def)) return;
    const absolute = joinPath(parentPath, def.path);
    const linkFn = (params?: Record<string, string | number>) => interpolate(absolute, params);
    out[def.key] = {
      link: linkFn,
      ...walk(def.children ?? [], absolute),
    };
  });
  return out;
};

// ─── Public API ──────────────────────────────────────────────────────────────

export interface BuildRouteHelperArgs<D extends readonly RouteDef[]> {
  routeDefs: D;
}

/**
 * Walk a `RouteDef[]` tree and produce a nested helper whose leaves build
 * absolute URLs. Each node exposes a `link(params?)` callable for the
 * node's own URL, plus a nested object per child route keyed by `key`.
 *
 * Designed to consume the same literal tree that `RenderRoutes` consumes,
 * declared with `defineRoutes([...] as const)` so the helper's types are
 * derived from the literal path strings. (`as const satisfies …` would be
 * cleaner but our pinned TypeScript version does not yet support it.)
 *
 * Param input types are inferred from each `path` string. Params declared
 * in `optionalParams` are optional in the input. Params declared in
 * `paramConstraints` narrow further: `'uuid'` keeps the param as `string`,
 * and an `EnumConstraint` narrows to the declared literal union so typos
 * are caught at compile time.
 *
 * Redirects are skipped — they are not navigation targets.
 *
 * @example
 * ```ts
 * const appRoutes = [
 *   { key: 'home', path: '/', Page: Home },
 *   {
 *     key: 'projects',
 *     path: '/projects',
 *     Page: ProjectsShell,
 *     children: [
 *       { key: 'search',  path: '',           Page: Search },
 *       { key: 'details', path: ':projectId', Page: Details },
 *     ],
 *   },
 * ] as const satisfies readonly RouteDef[];
 *
 * const routeHelper = buildRouteHelper({ routeDefs: appRoutes });
 * routeHelper.home.link();                                    // '/'
 * routeHelper.projects.link();                                // '/projects'
 * routeHelper.projects.details.link({ projectId: 42 });       // '/projects/42'
 * ```
 */
export const buildRouteHelper = <D extends readonly RouteDef[]>({ routeDefs }: BuildRouteHelperArgs<D>): Helper<D> =>
  walk(routeDefs) as Helper<D>;

/**
 * Identity helper that preserves the literal types of a `RouteDef[]` tree.
 * TS 4.8 lacks the `satisfies` operator, so call sites use
 * `defineRoutes([...] as const)` to keep `path` strings as literal types for
 * `buildRouteHelper` inference while still type-checking against `RouteDef`.
 */
export const defineRoutes = <D extends readonly RouteDef[]>(defs: D): D => defs;
